import {
  throttle,
  put,
  select,
  all,
  fork,
  spawn,
  call,
  take,
  takeEvery,
} from 'redux-saga/effects';
import {
  getContactOffset,
  getCompanyOffset,
  getContactLastSync,
  getCompanyLastSync,
  getHasCompletedRegistration,
  getActiveProjectTabView,
} from '../apps/main/rootReducer';
import {
  FETCH_CONTACTS_ME_ROLLBACK,
  SYNC_CONTACTS_ME,
  fetchContactsOffset,
} from '../modules/contacts/actions';
import { SYNC_COMPANIES, fetchCompanies } from '../modules/companies/actions';
import moment from 'moment';
import {
  ActiveProjectTabViewAction,
  FETCH_PROJECT_NEWS,
  fetchProjectNews,
  fetchProjectNewsCounts,
  SET_ACTIVE_PROJECT_TAB_VIEW,
  SYNC_PROJECTS,
  SYNC_PROJECTS_REQUEST,
  SYNC_PROJECTS_COMMIT,
  SYNC_PROJECTS_ROLLBACK,
  syncProjects,
} from '../modules/projects/actions';
import { notification } from 'antd';
import i18n from '../i18n';
import { SYNC_ABSENCES_ME, fetchAbsencesMe } from '../modules/absences/actions';
import { ReduxAction } from '../models/Redux';
import { DateTimeString } from '../models/Types';
import {
  SYNC_TIMERECORDS_ME,
  fetchMyTimeRecords,
} from '../modules/timeRecords/actions';
import {
  SYNC_MONTHLY_CLOSE_ME,
  fetchMonthlyCloseMe,
} from '../modules/timeKeeping/actions';
import { dispatchSaga } from '../util/sagas';
import {
  FETCH_USER_REGISTRATION_STATE_COMMIT,
  fetchUserRegistrationState,
  SYNC_USER_REGISTRATION_STATE,
} from '../modules/users/actions/registrationState';
import { LOGGED_IN } from '../modules/auth/actions';
import { apiUrl } from '../api';
import { getAccessToken } from '../store/authEffect';
import fetchWithRetry from '../util/fetchWithRetry';
import { Project } from '../models/Project';
import {
  GenericSearchResult,
  GenericSearchResultItem,
} from '../components/Filter/types';

function* syncContactsMe() {
  try {
    const offset = yield select(getContactOffset);
    const lastSync = yield select(getContactLastSync);
    const today = moment().toISOString().slice(0, 23);
    const refresh: boolean = moment(lastSync).diff(moment(today), 'days') > 3;

    if (refresh || !offset) {
      yield call(dispatchSaga, fetchContactsOffset());
    } else if (offset) {
      yield call(dispatchSaga, fetchContactsOffset(offset));
    }
  } catch (error) {
    console.error('Error in syncContactsMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('contacts:errorMessages.fetchError'),
    });
    yield put({
      type: FETCH_CONTACTS_ME_ROLLBACK,
    });
  }
}
function* syncCompanies() {
  try {
    const offset = yield select(getCompanyOffset);
    const lastSync = yield select(getCompanyLastSync);
    const today = moment().toISOString();
    const refresh = moment(today).diff(moment(lastSync), 'days') > 3;
    const validOffset = offset?.replace(/:(\d{3})$/, '.$1Z');
    yield call(dispatchSaga, fetchCompanies(refresh ? null : validOffset));
  } catch (error) {
    console.error('Error in syncCompanies', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('companies:errorMessages.fetchError'),
    });
  }
}
function* performProjectsSync(): any {
  yield put({ type: SYNC_PROJECTS_REQUEST });
  try {
    const token: string = yield call(getAccessToken);
    const headers = { Authorization: `Bearer ${token}` };

    const [meRes, overviewRes]: [Response, Response] = yield all([
      call(
        fetchWithRetry,
        `${apiUrl}/project/Project/me?includeArchived=true`,
        { headers }
      ),
      call(
        fetchWithRetry,
        `${apiUrl}/search/Search?searchType=projects&forceThreshold=100000&forceResult=true`,
        { headers }
      ),
    ]);

    if (!meRes.ok || !overviewRes.ok) throw new Error('Projects fetch failed');

    const [meProjects, overviewData]: [
      Project[],
      GenericSearchResult<Project>,
    ] = yield all([call([meRes, 'json']), call([overviewRes, 'json'])]);

    const meIds = new Set(meProjects.map((p) => p.projectId));
    const overviewProjects = (
      (overviewData?.items ?? []) as GenericSearchResultItem<Project>[]
    ).filter(({ data }) => !meIds.has(data?.projectId));

    yield put({
      type: SYNC_PROJECTS_COMMIT,
      payload: { meProjects, overviewProjects },
    });
  } catch (error) {
    console.error('Error in performProjectsSync', error);
    yield put({ type: SYNC_PROJECTS_ROLLBACK });
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('projects:errorMessages.fetchError'),
    });
  }
}

function* watchSyncProjects() {
  yield all([
    throttle(
      10 * 60 * 1000,
      (action: any) => {
        return action.type === SYNC_PROJECTS && !action.meta?.force;
      },
      performProjectsSync
    ),
    takeEvery(
      (action: any) => action.type === SYNC_PROJECTS && action.meta?.force,
      performProjectsSync
    ),
  ]);
}

function* syncAbsencesMe(
  action: ReduxAction<{
    from?: DateTimeString;
    to?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { from, to },
    } = action;
    yield spawn(dispatchSaga, fetchAbsencesMe(from, to));
  } catch (error) {
    console.error('Error in syncAbsencesMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('absences:errorMessages.fetchOverviewError'),
    });
  }
}
function* syncTimeRecordsMe(
  action: ReduxAction<{
    startDate?: DateTimeString;
    endDate?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { startDate, endDate },
    } = action;
    yield spawn(dispatchSaga, fetchMyTimeRecords(startDate, endDate));
  } catch (error) {
    console.error('Error in syncTimeRecordsMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('timeRecords:errorMessages.fetchError'),
    });
  }
}
function* syncMonthlyCloseMe(
  action: ReduxAction<{
    from?: DateTimeString;
    to?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { from, to },
    } = action;
    yield spawn(dispatchSaga, fetchMonthlyCloseMe(from, to));
  } catch (error) {
    console.error('Error in syncMonthlyCloseMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('timeKeeping:messages.errorMessages.fetchError'),
    });
  }
}

function* syncProjectNews() {
  try {
    yield put(fetchProjectNews());
    yield put(fetchProjectNewsCounts());
  } catch (error) {
    console.error('Error in watchFetchProjectNews', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('projects:errorMessages.fetchError'),
    });
  }
}

function* syncUserRegistrationState() {
  try {
    const hasCompletedRegistration = yield select(getHasCompletedRegistration);
    if (!hasCompletedRegistration) {
      yield put(fetchUserRegistrationState());
    } else {
      yield put({ type: FETCH_USER_REGISTRATION_STATE_COMMIT });
    }
  } catch (error) {
    console.error('Error in syncUserRegistrationState', error);
  }
}

function* watchSyncContacts() {
  yield throttle(60000, SYNC_CONTACTS_ME, syncContactsMe);
}
function* watchSyncCompanies() {
  yield throttle(60000, SYNC_COMPANIES, syncCompanies);
}
function* watchSyncAbsencesMe() {
  yield throttle(60000, SYNC_ABSENCES_ME, syncAbsencesMe);
}
function* watchSyncTimerecordsMe() {
  yield throttle(60000, SYNC_TIMERECORDS_ME, syncTimeRecordsMe);
}
function* watchSyncMonthlyCloseMe() {
  yield throttle(60000, SYNC_MONTHLY_CLOSE_ME, syncMonthlyCloseMe);
}

function* watchFetchProjectNews() {
  yield throttle(60000, FETCH_PROJECT_NEWS, syncProjectNews);
}

function* watchSyncUserRegistrationState() {
  yield take(SYNC_USER_REGISTRATION_STATE);
  yield call(syncUserRegistrationState);
}

function* syncProjectsForce() {
  yield put(syncProjects());
}

function* watchSyncProjectTabs() {
  yield take(LOGGED_IN);
  const activeTabView = yield select(getActiveProjectTabView);
  if (activeTabView === 'all') {
    yield put(syncProjects());
  }
  yield throttle(
    20000,
    (action: ActiveProjectTabViewAction) => {
      if (action.type === SET_ACTIVE_PROJECT_TAB_VIEW) {
        return action.activeTab === 'all';
      }
      return false;
    },
    syncProjectsForce
  );
}

export default function* watchSyncActions() {
  yield all([
    fork(watchSyncUserRegistrationState),
    fork(watchSyncContacts),
    fork(watchSyncCompanies),
    fork(watchSyncAbsencesMe),
    fork(watchSyncTimerecordsMe),
    fork(watchSyncMonthlyCloseMe),
    fork(watchFetchProjectNews),
    fork(watchSyncProjectTabs),
    fork(watchSyncProjects),
  ]);
}
